import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, AlertCircle, ArrowLeft, Sparkles } from "lucide-react";
import FaceCapture from "@/components/FaceCapture";

const ICT_OFFSET_MIN = 330; // IST = UTC+5:30

const istNow = () => new Date(Date.now() + ICT_OFFSET_MIN * 60 * 1000);
const isWednesdayIST = () => istNow().getUTCDay() === 3;

const KnoCheckIn = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [attempts, setAttempts] = useState(0);

    const demoActive = useMemo(
        () => searchParams.get("demo") === "1" && user?.role === "super_admin",
        [searchParams, user]
    );

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/profile/face/status");
            setStatus(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleEmbedding = async (embedding) => {
        setSubmitting(true);
        try {
            const url = demoActive ? "/kno/self-mark?demo=1" : "/kno/self-mark";
            const r = await api.post(url, { embedding });
            if (r.data?.ok && r.data?.matched) {
                toast.success(r.data?.demo ? "Demo: marked present (super admin bypass)." : "You're marked present for today's KNO.");
                setResult({ success: true, confidence: r.data.confidence, session_date: r.data.session_date, already: !!r.data.already_marked, demo: !!r.data.demo });
            } else {
                setAttempts((a) => a + 1);
                setResult({ success: false, message: r.data?.message || "Face did not match. Try again.", attempts_remaining: r.data?.attempts_remaining ?? null });
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || "Check-in failed";
            const code = e?.response?.status;
            setResult({ success: false, fatal: code === 429 || code === 400 || code === 403, message: detail });
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-16 text-slate-500 text-sm">Loading…</div>;
    }

    // Gating states
    const wed = isWednesdayIST() || demoActive;
    const featureOn = !!status?.feature_enabled;
    const enrolled = !!status?.enrolled;

    return (
        <div className="space-y-6 max-w-xl mx-auto pb-16" data-testid="kno-checkin-page">
            <button
                onClick={() => navigate("/kno")}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
                data-testid="kno-checkin-back"
            >
                <ArrowLeft className="h-4 w-4" /> Back to KNO
            </button>

            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Self check-in</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1 inline-flex items-center gap-3">
                    <GraduationCap className="h-7 w-7 text-[#2EB4AC]" />
                    KNO Attendance
                </h1>
                <p className="text-slate-500 mt-2 text-sm">
                    Hey {user?.name?.split(" ")[0]} — match your face to the one we have on file to mark yourself present.
                </p>
            </div>

            {demoActive && (
                <div className="border border-purple-200 bg-purple-50 text-purple-800 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="kno-checkin-demo-banner">
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        <span className="font-semibold">Demo mode</span> — Wednesday check bypassed (super admin only). The mark will be stamped on the nearest Wednesday and is fully audited.
                    </div>
                </div>
            )}

            {!featureOn && (
                <GatingCard tone="slate" icon={AlertCircle} title="Self check-in is disabled" message="Your organization has turned this off. Ask a coordinator to mark you manually." testid="kno-checkin-disabled" />
            )}

            {featureOn && !wed && (
                <GatingCard tone="amber" icon={AlertCircle} title="Not a Wednesday" message="Self check-in is only available on Wednesdays, the day of the session." testid="kno-checkin-not-wed" />
            )}

            {featureOn && wed && !enrolled && (
                <GatingCard
                    tone="teal"
                    icon={Sparkles}
                    title="Enroll your face first"
                    message="You haven't enrolled yet. It's a one-time step from your Profile."
                    testid="kno-checkin-not-enrolled"
                    action={{ label: "Go to Profile", onClick: () => navigate("/profile") }}
                />
            )}

            {featureOn && wed && enrolled && !result?.success && (
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="kno-checkin-capture">
                    <FaceCapture
                        onEmbedding={handleEmbedding}
                        mode="match"
                        autoStart={true}
                    />
                    {result && !result.success && (
                        <div
                            className={`mt-4 flex items-start gap-2 p-3 rounded-lg border text-sm ${result.fatal ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                            data-testid="kno-checkin-result-fail"
                        >
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>
                                {result.message}
                                {result.attempts_remaining != null && !result.fatal && (
                                    <div className="text-xs mt-0.5 opacity-80">Attempts remaining today: {result.attempts_remaining}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {result?.success && (
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center" data-testid="kno-checkin-success">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                        <CheckCircle2 className="h-9 w-9" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">You're checked in</h2>
                    <p className="text-slate-500 mt-1 text-sm">
                        {result.already ? "Already marked present for " : "Marked present for "}
                        {result.session_date || "today"}.
                        {typeof result.confidence === "number" && (
                            <span className="block text-xs text-slate-400 mt-1">Match confidence: {(result.confidence * 100).toFixed(0)}%</span>
                        )}
                    </p>
                    <button
                        onClick={() => navigate("/dashboard")}
                        data-testid="kno-checkin-success-done"
                        className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium transition-colors"
                    >
                        Back to dashboard
                    </button>
                </div>
            )}

            <div className="text-[11px] text-slate-400 text-center">
                Your photo never leaves this device. Only a 128-number fingerprint is used to match.
            </div>
        </div>
    );
};

const GatingCard = ({ tone, icon: Icon, title, message, action, testid }) => {
    const palette = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-800",
        teal: "bg-teal-50 border-teal-200 text-[#25908A]",
    }[tone];
    return (
        <div className={`border rounded-lg p-5 ${palette}`} data-testid={testid}>
            <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="font-semibold">{title}</div>
                    <div className="text-sm mt-1 opacity-90">{message}</div>
                    {action && (
                        <button onClick={action.onClick} className="mt-3 inline-flex h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-medium transition-colors">
                            {action.label}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KnoCheckIn;
